/**
 * Bazaarvoice Reviews — Contentful placeholder for PDP customer reviews (DE-116).
 *
 * Decouples the Bazaarvoice reviews section from the hardcoded PDP template so
 * editors can place/order it in a Page / Container region. Product data comes
 * from page context (not Contentful fields).
 *
 * Resilience (content type / entry removed or unpublished):
 * - Container GraphQL uses `... on Entry` + null-filters unavailable refs, so
 *   a missing type does not fail the page query.
 * - Metadata marks this type complete with `sys` only and disables by-id fetch
 *   so we never call `bazaarvoiceReviews(id:)` after the schema field is gone.
 * - This model never reads CMS fields; on PDP it only mounts ProductBazaarvoice
 *   when product context exists, otherwise renders nothing (site stays up).
 *
 * Out of scope: inline star-rating in the add-to-cart area.
 */

import {Component, useLayoutEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertTitle} from '@chakra-ui/react';

import {AlertIcon} from '@amblique/king-living/app/components/icons';
import ProductBazaarvoice from '@amblique/king-living/app/components/product-bazaarvoice';
import {useBazaarvoiceReviewsPresence} from '@amblique/king-living/app/context/bazaarvoice-reviews-presence-context';
import {usePageContext} from '@amblique/king-living/app/hooks/contentful';
import Alert from '@amblique/king-living/app/theme/components/base/alert';

/**
 * Local boundary so a BV widget runtime error cannot take down the PDP.
 */
class BazaarvoiceReviewsBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[BazaarvoiceReviews] suppressed render error', error);
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

BazaarvoiceReviewsBoundary.propTypes = {
  children: PropTypes.node,
};

/**
 * @param {Object} props
 * @param {Contentful.BazaarvoiceReviews} props.component
 * @param {boolean} [props.isComplete]
 * @param {boolean} [props.isFetching]
 * @returns {React.ReactElement|null}
 */
export const BazaarvoiceReviews = ({component, isComplete = true, isFetching = false}) => {
  const pageContext = usePageContext() || {};
  const {isEdit, isProductPage, product} = pageContext;
  const presence = useBazaarvoiceReviewsPresence();
  const markPresent = presence?.markPresent;

  // Tell the page-level fallback to unmount so CMS + fallback never both show.
  useLayoutEffect(() => {
    if (isProductPage && product) {
      markPresent?.();
    }
  }, [isProductPage, product, markPresent]);

  // Keep ProductBazaarvoice's fixed `#product-bazaarvoice-reviews` id so inline
  // star-rating deep links (out of scope for DE-116) continue to work.
  if (isProductPage) {
    if (!product) {
      return null;
    }

    return (
      <BazaarvoiceReviewsBoundary>
        <ProductBazaarvoice product={product} />
      </BazaarvoiceReviewsBoundary>
    );
  }

  if (isEdit) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertTitle>Bazaarvoice Reviews</AlertTitle>

        <FormattedMessage
          defaultMessage="This component can only be added to '{aspectType}' pages."
          id="contentful.error.invalid_aspects"
          values={{aspectType: 'Product Detail'}}
        />
      </Alert>
    );
  }

  // Homepage / non-PDP / missing CMS entry after type removal → render nothing.
  return null;
};

BazaarvoiceReviews.displayName = 'BazaarvoiceReviews';
BazaarvoiceReviews.propTypes = {
  component: PropTypes.object,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
